// import React from "react";
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Skeleton, Button } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { updateSEO } from "../utils/SEO";

const imagePaths = ["/services01.jpg", "/services02.jpg", "/services03.jpg"];

const Services = () => {
  const [loadedImages, setLoadedImages] = useState({});

  const handleImageLoad = (index) => {
    setLoadedImages(prev => ({ ...prev, [index]: true }));
  };

  useEffect(() => {
    updateSEO({
      title: "Interior Design Services | Full Service Design, Design Day & Consultations | Seattle, WA",
      description: "Nicole Hahn Designs offers full service interior design for new construction and remodels, Design Day consultations, and in-home consultations. Serving Seattle, Bellevue, Tacoma, and surrounding areas.",
      url: "https://nicolehahndesigns.com/services",
      image: "https://nicolehahndesigns.com/services01.jpg"
    });

    // Add enhanced structured data for Services page
    const serviceScript = document.createElement('script');
    serviceScript.type = 'application/ld+json';
    serviceScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Interior Design Services",
      "provider": {
        "@type": "HomeAndConstructionBusiness",
        "name": "Nicole Hahn Designs",
        "url": "https://nicolehahndesigns.com",
        "email": "info@nicolehahndesigns.com"
      },
      "areaServed": [
        {
          "@type": "Place",
          "name": "Seattle, WA"
        },
        {
          "@type": "Place",
          "name": "Bellevue, WA"
        },
        {
          "@type": "Place",
          "name": "Tacoma, WA"
        },
        {
          "@type": "Place",
          "name": "King County, WA"
        },
        {
          "@type": "Place",
          "name": "Pierce County, WA"
        }
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Interior Design Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Full Service - New Construction and Remodels",
              "description": "Complete interior design services for custom new construction homes and remodels. Services include: floor plan design, architectural material selection, finishes and fixtures selection, custom cabinetry design, furniture sourcing, and project management from concept to final installation.",
              "provider": {
                "@type": "HomeAndConstructionBusiness",
                "name": "Nicole Hahn Designs"
              }
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Design Day",
              "description": "A detailed, high-quality design plan service for clients who prefer to implement designs themselves. Ideal for single room design or selecting key pieces for multiple spaces. Provides flexibility and expert guidance without full-service commitment. Starting at approximately $10,000.",
              "provider": {
                "@type": "HomeAndConstructionBusiness",
                "name": "Nicole Hahn Designs"
              }
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Consultations",
              "description": "2-hour in-home consultation service for paint color selection, material review for remodels or new builds, conceptual space planning, furniture selection, and general design guidance. Clients choose the focus area during the session.",
              "provider": {
                "@type": "HomeAndConstructionBusiness",
                "name": "Nicole Hahn Designs"
              }
            }
          }
        ]
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://nicolehahndesigns.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Services",
            "item": "https://nicolehahndesigns.com/services"
          }
        ]
      }
    });
    document.head.appendChild(serviceScript);

    // Add HowTo schema for design process
    const howToScript = document.createElement('script');
    howToScript.type = 'application/ld+json';
    howToScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to Work with an Interior Designer - Full Service Process",
      "description": "The process of working with Nicole Hahn Designs for full service interior design projects",
      "step": [
        {
          "@type": "HowToStep",
          "position": 1,
          "name": "Initial Consultation",
          "text": "Contact Nicole Hahn Designs to schedule a discovery call. Discuss your project goals, timeline, budget, and design preferences."
        },
        {
          "@type": "HowToStep",
          "position": 2,
          "name": "Floor Plan Design",
          "text": "We create a floor plan that supports the architectural integrity of your home and creates the flow you're striving for."
        },
        {
          "@type": "HowToStep",
          "position": 3,
          "name": "Material and Finish Selection",
          "text": "Once the layout is finalized, we select architectural materials, finishes, and fixtures that align with your style and budget."
        },
        {
          "@type": "HowToStep",
          "position": 4,
          "name": "Custom Design Elements",
          "text": "We handle custom cabinetry design and source exclusive furniture pieces to ensure a unified design."
        },
        {
          "@type": "HowToStep",
          "position": 5,
          "name": "Project Management",
          "text": "We support you throughout the entire process, managing all design elements from concept to final installation."
        }
      ],
      "totalTime": "PT3M"
    });
    document.head.appendChild(howToScript);

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => {
        const text = script.text;
        if (text.includes('"@type": "Service"') || text.includes('"@type": "HowTo"')) {
          script.remove();
        }
      });
    };
  }, []);

  return (
    <Box
      component="main"
      sx={{
        width: "100%",
        textAlign: "center",
      }}
    >
      {/* Image Section */}
      <Box
        component="section"
        aria-label="Service Images"
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: { xs: "10px", md: "5px" },
          flexWrap: "wrap",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          backgroundColor: "#F5F5F5",
          paddingBottom: { xs: "2rem", md: "5rem" }
        }}
      >
        {imagePaths.map((image, index) => (
          <Box
            key={index}
            sx={{
              width: { xs: "100%", md: "33.1%" },
              height: { xs: "500px", md: "600px" },
              position: "relative",
              overflow: "hidden",
              transition: "transform 0.3s ease-in-out",
              display: { xs: index === 1 ? "block" : "none", md: "block" }
            }}
          >
            {!loadedImages[index] && (
              <Skeleton
                variant="rectangular"
                width="100%"
                height="100%"
                animation="wave"
              />
            )}
            <Box
              component="img"
              src={image}
              alt={`${index === 0 ? 'Full service interior design and custom new construction' : index === 1 ? 'Design Day consultation and space planning services' : 'Interior design consultation and remodeling services'} by Nicole Hahn Designs in Seattle, Bellevue, and Tacoma, Washington`}
              loading="lazy"
              onLoad={() => handleImageLoad(index)}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                opacity: loadedImages[index] ? 1 : 0,
                transition: "opacity 0.3s ease-in-out",
              }}
            />
          </Box>
        ))}
      </Box>

      {/* Services Section */}
      <Box
        component="section"
        aria-label="Services Information"
        sx={{
          width: "100%",
          backgroundColor: "#F5F5F5",
          color: "black",
          paddingBottom: "3rem",
        }}
      >
        <Typography
          variant="h1"
          component="h1"
          sx={{
            fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
            fontWeight: { xs: 300, sm: 300, md: 700 },
            textAlign: { xs: "left", sm: "center", md: "center" },
            padding: { xs: "2rem 1rem 1rem", sm: "3rem 2rem 2rem", md: "4rem 2rem 2rem" },
            fontFamily: '"Bebas Neue", sans-serif',
            color: "#000000"
          }}
        >
          Interior Design Services
        </Typography>
        {[
          {
            title: "Full Service - New Construction and Remodels",
            content:
              "Whether it is a custom new construction home or re-imagining your kitchen, we focus on tailoring the design to your unique personality, needs, and lifestyle. We start by drawing a floor plan that best supports the architectural integrity of the home and creates the overall flow you are striving for. Once the layout is finalized, we move on to selecting architectural materials, finishes, and fixtures. We will handle everything from custom cabinetry design to sourcing exclusive furniture pieces to ensure a unified design is created and that you are supported throughout the entire process.",
          },
          {
            title: "Design Day",
            content:
              "We love being involved in our projects from the initial concept all the way through to final installation. However, we recognize that not all clients require the full-service approach. Some may just need help with the finishing touches of a few spaces while others may prefer to take a more hands-on role in the process. To cater to these needs, we developed our Design Day service. This offering provides clients with a detailed, high-quality design plan that clients can confidently execute at their own pace according to their schedule and budget. Design Day is ideal for designing a single room or selecting a few key pieces for different spaces. This service offers flexibility and expert guidance without the full commitment.",
          },
          // {
          //   title: "Furnishings",
          //   content:
          //     "We provide comprehensive furnishing, accessorizing, and styling services tailored to your needs. The process starts with an in-person consultation to understand your goals and preferences. Once we've defined the desired design aesthetic, we handle sourcing all the furniture, décor, lighting, accessories, and window treatments. Our white-glove service ensures everything is delivered and installed on the scheduled day. From coordinating delivery to styling and final touches, we manage every detail for a seamless, turn-key experience.",
          // },
          {
            title: "Consultations",
            content:
              "There are times when all you need is a little guidance to get things moving in the right direction. An in-home consultation is a 2-hour session where we can help with selecting paint colors, review material selections for a remodel or new build, conceptual space planning, choosing furniture, etc. You'll have the freedom to decide what you'd like to focus on during our time together so you can feel confident about your design.",
          },
        ].map((item, index) => (
          <Accordion
            key={index}
            sx={{
              background: "#F5F5F5",
              border: 'none',
              transition: "all 0.3s ease-in-out",
              "&:last-child": { marginBottom: 0 },
              "&:before": { display: "none" },
              "&.Mui-expanded": {
                margin: "8px 0",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "#333", transition: "transform 0.3s ease-in-out" }} />}
              aria-controls={`panel${index}-content`}
              id={`panel${index}-header`}
              sx={{
                background: "#e6e4df",
                padding: { xs: "1rem", sm: "1.2rem" },
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  backgroundColor: "#d6d4cf",
                  "& .MuiTypography-root": {
                    color: "#000",
                  }
                },
                "&.Mui-expanded": {
                  minHeight: "64px",
                }
              }}
            >
              <Typography
                component="h2"
                variant="h6"
                sx={{
                  fontSize: { xs: "1.3rem", sm: "1.5rem", md: "1.7rem" },
                  fontWeight: { xs: 300, sm: 300, md: 600 },
                  color: "#000000",
                  transition: "color 0.3s ease-in-out",
                  fontFamily: '"Bebas Neue", sans-serif',
                  textAlign: 'left',
                  width: '100%'
                }}
              >
                {item.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                padding: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
                background: "#F5F5F5",
                transition: "all 0.3s ease-in-out",
              }}
            >
              <Typography
                component="p"
                sx={{
                  fontSize: { xs: "1rem", sm: "1.2rem", md: "1.4rem" },
                  textAlign: "left",
                  lineHeight: { xs: 1.5, sm: 1.7, md: 1.8 },
                  color: "#000000",
                  width: "100%",
                  fontFamily: '"Old Standard TT", serif',
                  fontWeight: { xs: 300, sm: 300, md: 400 }
                }}
              >
                {item.content}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}

        {/* CTA Button */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: '3rem',
            padding: { xs: '0 1rem', sm: '0 2rem' },
            gap: '1.5rem'
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
              color: '#333',
              maxWidth: '800px',
              lineHeight: 1.5,
              fontFamily: '"Old Standard TT", serif'
            }}
          >
            To find out more about our design services please schedule a meeting!
          </Typography>
          <Button
            variant="contained"
            component={Link}
            to="/contact"
            sx={{
              padding: '1rem 2rem',
              backgroundColor: '#464d22',
              color: 'white',
              fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' },
              fontWeight: 500,
              letterSpacing: '0.02em',
              borderRadius: '20px',
              fontFamily: '"Bebas Neue", sans-serif',
              minWidth: { xs: "200px", sm: "250px", md: "300px" },
              '&:hover': {
                backgroundColor: '#757a5c',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              },
              transition: 'all 0.3s ease-in-out',
            }}
          >
            Schedule a Meeting
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Services;
